import React, { useCallback, useMemo, useState } from 'react';
import { ArrayInput, SimpleFormIterator, useTranslate } from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  Box,
  Button,
  Chip,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ScopedIteratorFields } from '../event-utils/ScopedIteratorFields';
import {
  StandardProductForm as SharedStandardProductForm,
  SeatedProductForm as SharedSeatedProductForm,
} from '../../stocks/StocksProductForm';
import { normalizeEventType } from '../../../utils/eventFormUtils';
import { generateTicketID } from '../../../utils/formatting';
import { useSourceLanguage } from '../../../hooks/useSourceLanguage';
import { SourceLanguageBanner } from '../SourceLanguageBanner';

/** Genel biletler kutusunda birleşen gruplar (davetiye hariç). */
const UNIFIED_TICKET_GROUPS = ['deal', 'guestlist', 'general', 'vip', 'extras'];

const GROUP_LABEL_KEYS = {
  deal: 'form.special_deal',
  guestlist: 'form.reservation',
  general: 'form.general_ticket_type',
  vip: 'form.vip_list',
  extras: 'form.extras',
};

const GROUP_LABEL_FALLBACKS = {
  deal: 'Special Deal',
  guestlist: 'Reservation',
  general: 'General Ticket',
  vip: 'VIP List',
  extras: 'Extras',
};

const PANEL_SX = {
  border: '1px solid #e5e7eb',
  borderRadius: '12px',
  bgcolor: '#fff',
  overflow: 'hidden',
  width: '100%',
  height: '100%',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
};

const PANEL_HEADER_SX = {
  px: 2,
  py: 1.5,
  borderBottom: '1px solid #eef0f3',
  bgcolor: '#fafbfc',
  display: 'flex',
  alignItems: 'baseline',
  gap: 1,
  flexWrap: 'wrap',
};

const PRODUCT_CARD_SX = {
  border: '1px solid #e8eaee',
  borderRadius: '10px',
  bgcolor: '#fff',
  p: { xs: 1.5, sm: 2 },
  mb: 1.5,
};

const productTitlePreview = (item, sourceLang) => {
  const title = item?.title;
  if (!title) return '';
  if (typeof title === 'string') return title.trim();
  if (typeof title === 'object') {
    return String(title[sourceLang] || title.tr || title.en || '').trim();
  }
  return '';
};

const emptyProduct = () => ({
  id: generateTicketID(),
  status: 0,
  refundable: false,
  hasDownPayment: false,
  hasBasketLimit: false,
  sold: 0,
  price: 0,
  stock: 0,
});

/**
 * Product Form Manager Component
 * Manages product/ticket forms based on event type
 */
export const ProductFormManager = ({
  eventType,
  disabled = false,
  isEdit = false,
  record = {},
}) => {
  const formType = useWatch({ name: 'type' });
  const resolvedType = normalizeEventType(formType ?? eventType);
  const { sourceLang } = useSourceLanguage({ isEdit, record });

  if (resolvedType === 1 || resolvedType === 2) {
    return (
      <StandardProductForm
        disabled={disabled}
        sourceLang={sourceLang}
        record={record}
        isEdit={isEdit}
      />
    );
  }
  if (resolvedType === 3) {
    return (
      <SeatedProductForm
        disabled={disabled}
        sourceLang={sourceLang}
        record={record}
        isEdit={isEdit}
      />
    );
  }
  if (eventType === 1 || eventType === 2) {
    return (
      <StandardProductForm
        disabled={disabled}
        sourceLang={sourceLang}
        record={record}
        isEdit={isEdit}
      />
    );
  }
  if (eventType === 3) {
    return (
      <SeatedProductForm
        disabled={disabled}
        sourceLang={sourceLang}
        record={record}
        isEdit={isEdit}
      />
    );
  }
  return null;
};

/**
 * Standard Product Form — sol: Genel Biletler, sağ: Davetiyeler
 */
const StandardProductForm = ({ disabled = false, sourceLang, record, isEdit }) => (
  <React.Fragment>
    <SourceLanguageBanner isEdit={isEdit} record={record} />
    <StandardProductsEditor disabled={disabled} sourceLang={sourceLang} isEdit={isEdit} />
  </React.Fragment>
);

/**
 * Genel Biletler + Davetiyeler. Oluşturmada `tickets`, düzenlemede `_stockRecord.stock` altında çalışır.
 */
export const StandardProductsEditor = ({
  disabled = false,
  sourceLang,
  basePath = 'tickets',
  isEdit = false,
}) => {
  const translate = useTranslate();
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'minmax(0, 1fr) minmax(0, 1fr)',
        },
        gap: { xs: 2, sm: 2.5 },
        width: '100%',
        maxWidth: '100%',
        alignItems: 'stretch',
        boxSizing: 'border-box',
        '& > *': {
          minWidth: 0,
          width: '100%',
          height: '100%',
        },
      }}
    >
      <UnifiedGeneralTicketsSection
        disabled={disabled}
        sourceLang={sourceLang}
        basePath={basePath}
        isEdit={isEdit}
      />
      <ProductFormSection
        title={translate('form.invitations', 'Invitations')}
        source={`${basePath}.invitation`}
        disabled={disabled}
        sourceLang={sourceLang}
        isEdit={isEdit}
        isInvitation
        helperText={translate(
          'form.invitations_hint',
          'Can create invitations for guests to attend the event.',
        )}
      />
    </Box>
  );
};

/**
 * Seated Product Form Component
 * For Seated events
 */
const SeatedProductForm = ({ disabled = false, sourceLang, record, isEdit }) => {
  const translate = useTranslate();
  return (
    <React.Fragment>
      <SourceLanguageBanner isEdit={isEdit} record={record} />
      <ArrayInput source="tickets" label={translate('form.tickets', 'Tickets')} defaultValue={[]}>
        <SimpleFormIterator
          disableReordering
          className="sessionEventTicketsArray"
          disableRemove={disabled}
          disableAdd={disabled}
        >
          <ScopedIteratorFields>
            {({ scopedFormData, getSource }) => (
              <SeatedProductFormFields
                getSource={getSource}
                scopedFormData={scopedFormData}
                disabled={disabled}
                sourceLang={sourceLang}
              />
            )}
          </ScopedIteratorFields>
        </SimpleFormIterator>
      </ArrayInput>
    </React.Fragment>
  );
};

/**
 * deal / guestlist / general / vip / extras → tek "Genel Biletler" kutusu.
 * Artıya basmadan önce tip select ile grup seçilir; yeni ürün her zaman alta eklenir.
 */
const UnifiedGeneralTicketsSection = ({
  disabled = false,
  sourceLang,
  basePath = 'tickets',
  isEdit = false,
}) => {
  const translate = useTranslate();
  const { getValues, setValue } = useFormContext();
  const [addGroup, setAddGroup] = useState('general');
  /** Ekleme sırası (ticket id) — yeni ürün her zaman listenin altına gider. */
  const [displayOrder, setDisplayOrder] = useState([]);

  const deal = useWatch({ name: `${basePath}.deal` });
  const guestlist = useWatch({ name: `${basePath}.guestlist` });
  const general = useWatch({ name: `${basePath}.general` });
  const vip = useWatch({ name: `${basePath}.vip` });
  const extras = useWatch({ name: `${basePath}.extras` });

  const groupChoices = useMemo(
    () =>
      UNIFIED_TICKET_GROUPS.map((id) => ({
        id,
        name: translate(GROUP_LABEL_KEYS[id], { _: GROUP_LABEL_FALLBACKS[id] }),
      })),
    [translate],
  );

  const locatedItems = useMemo(() => {
    const byGroup = {
      deal: Array.isArray(deal) ? deal : [],
      guestlist: Array.isArray(guestlist) ? guestlist : [],
      general: Array.isArray(general) ? general : [],
      vip: Array.isArray(vip) ? vip : [],
      extras: Array.isArray(extras) ? extras : [],
    };
    const byId = new Map();
    UNIFIED_TICKET_GROUPS.forEach((group) => {
      byGroup[group].forEach((item, index) => {
        const id = item?.id || `${group}-${index}`;
        byId.set(id, { group, index, item, id });
      });
    });
    return byId;
  }, [deal, guestlist, general, vip, extras]);

  const compareOrder = (aId, bId) => {
    const a = Number(locatedItems.get(aId)?.item?.order);
    const b = Number(locatedItems.get(bId)?.item?.order);
    const aHas = Number.isFinite(a);
    const bHas = Number.isFinite(b);
    if (aHas && bHas && a !== b) return a - b;
    if (aHas && !bHas) return -1;
    if (!aHas && bHas) return 1;
    return 0;
  };

  // Bilinen ürünlerle displayOrder'ı senkron tut; ilk yüklemede order alanına göre sırala
  React.useEffect(() => {
    const existingIds = Array.from(locatedItems.keys());
    setDisplayOrder((prev) => {
      if (prev.length === 0) {
        return [...existingIds].sort(compareOrder);
      }
      const kept = prev.filter((id) => locatedItems.has(id));
      const missing = existingIds.filter((id) => !kept.includes(id)).sort(compareOrder);
      if (kept.length === prev.length && missing.length === 0) return prev;
      return [...kept, ...missing];
    });
    // compareOrder depends on locatedItems; intentional
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locatedItems]);

  /** İlk yüklemedeki order yazımı formu kirli göstermesin. */
  const initialOrderSyncedRef = React.useRef(false);

  const syncOrderFields = useCallback(
    (orderedIds) => {
      const markDirty = initialOrderSyncedRef.current;
      initialOrderSyncedRef.current = true;
      UNIFIED_TICKET_GROUPS.forEach((group) => {
        const list = getValues(`${basePath}.${group}`);
        if (!Array.isArray(list) || !list.length) return;
        let changed = false;
        const next = list.map((item) => {
          if (!item?.id) return item;
          const orderIndex = orderedIds.indexOf(item.id);
          if (orderIndex < 0 || Number(item.order) === orderIndex) return item;
          changed = true;
          return { ...item, order: orderIndex };
        });
        if (changed) {
          setValue(`${basePath}.${group}`, next, {
            shouldDirty: markDirty,
            shouldTouch: markDirty,
          });
        }
      });
    },
    [basePath, getValues, setValue],
  );

  // Panel sırasını ürün.order alanına yaz (sitede buna göre listelenir)
  React.useEffect(() => {
    if (!displayOrder.length) return;
    syncOrderFields(displayOrder);
  }, [displayOrder, syncOrderFields]);

  const flatItems = useMemo(() => {
    const rows = [];
    const seen = new Set();
    displayOrder.forEach((id) => {
      const row = locatedItems.get(id);
      if (row) {
        rows.push(row);
        seen.add(id);
      }
    });
    locatedItems.forEach((row, id) => {
      if (!seen.has(id)) rows.push(row);
    });
    return rows;
  }, [displayOrder, locatedItems]);

  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  /** Kart yalnızca tutamaçtan tutulunca draggable olur; input içinde metin seçimi bozulmasın. */
  const [armedId, setArmedId] = useState(null);

  const toggleExpanded = useCallback((itemId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  }, []);

  const resetDrag = useCallback(() => {
    setDragId(null);
    setOverId(null);
    setArmedId(null);
  }, []);

  React.useEffect(() => {
    if (!armedId) return undefined;
    const disarm = () => {
      if (!dragId) setArmedId(null);
    };
    window.addEventListener('mouseup', disarm);
    return () => window.removeEventListener('mouseup', disarm);
  }, [armedId, dragId]);

  const handleDragStart = useCallback(
    (itemId) => (event) => {
      if (disabled || armedId !== itemId) {
        event.preventDefault();
        return;
      }
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', itemId);
      setDragId(itemId);
    },
    [armedId, disabled],
  );

  const handleDragOver = useCallback(
    (itemId) => (event) => {
      if (!dragId) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      if (overId !== itemId) setOverId(itemId);
    },
    [dragId, overId],
  );

  const handleDrop = useCallback(
    (targetId) => (event) => {
      event.preventDefault();
      if (!dragId || dragId === targetId) {
        resetDrag();
        return;
      }
      setDisplayOrder((prev) => {
        const from = prev.indexOf(dragId);
        const to = prev.indexOf(targetId);
        if (from < 0 || to < 0) return prev;
        const next = [...prev];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        return next;
      });
      resetDrag();
    },
    [dragId, resetDrag],
  );

  const handleAdd = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (disabled || !addGroup) return;
      const product = emptyProduct();
      const source = `${basePath}.${addGroup}`;
      const current = getValues(source);
      const list = Array.isArray(current) ? [...current] : [];
      list.push(product);
      setValue(source, list, { shouldDirty: true, shouldTouch: true });
      setDisplayOrder((prev) => [...prev, product.id]);
      setExpandedIds((prev) => new Set(prev).add(product.id));
    },
    [addGroup, basePath, disabled, getValues, setValue],
  );

  const handleRemove = useCallback(
    (group, index, itemId) => (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (disabled) return;
      const source = `${basePath}.${group}`;
      const current = getValues(source);
      const list = Array.isArray(current) ? [...current] : [];
      list.splice(index, 1);
      setValue(source, list, { shouldDirty: true, shouldTouch: true });
      if (itemId) {
        setDisplayOrder((prev) => prev.filter((id) => id !== itemId));
      }
    },
    [basePath, disabled, getValues, setValue],
  );

  const handleChangeGroup = useCallback(
    (fromGroup, fromIndex, toGroup) => {
      if (disabled || !toGroup || toGroup === fromGroup) return;
      const fromPath = `${basePath}.${fromGroup}`;
      const toPath = `${basePath}.${toGroup}`;
      const fromList = Array.isArray(getValues(fromPath)) ? [...getValues(fromPath)] : [];
      if (fromIndex < 0 || fromIndex >= fromList.length) return;
      const [moved] = fromList.splice(fromIndex, 1);
      const toList = Array.isArray(getValues(toPath)) ? [...getValues(toPath)] : [];
      toList.push(moved);
      setValue(fromPath, fromList, { shouldDirty: true, shouldTouch: true });
      setValue(toPath, toList, { shouldDirty: true, shouldTouch: true });
      // Tip değişince sırayı koru (displayOrder id üzerinden zaten sabit)
    },
    [basePath, disabled, getValues, setValue],
  );

  /** İade ayarını yalnızca listede bu ürünün altındakilere kopyalar. */
  const handleApplyRefundBelow = useCallback(
    (listIndex, group, index) => {
      if (disabled) return;
      const refundable = getValues(`${basePath}.${group}.${index}.refundable`);
      const deadline = getValues(`${basePath}.${group}.${index}.refundDeadlineAt`) ?? null;
      flatItems.slice(listIndex + 1).forEach((row) => {
        const base = `${basePath}.${row.group}.${row.index}`;
        setValue(`${base}.refundable`, Boolean(refundable), { shouldDirty: true });
        setValue(`${base}.refundDeadlineAt`, deadline, {
          shouldDirty: true,
          shouldValidate: true,
        });
      });
    },
    [basePath, disabled, flatItems, getValues, setValue],
  );

  return (
    <Box sx={PANEL_SX}>
      <Box sx={PANEL_HEADER_SX}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ m: 0, color: '#111827' }}>
          {translate('form.general_ticket', 'General Tickets')}
        </Typography>
        <Typography variant="caption" sx={{ color: '#b45309', fontStyle: 'italic' }}>
          {translate('form.all_fields_required', '(* all fields required)')}
        </Typography>
        <Typography variant="caption" sx={{ color: '#6b7280', width: '100%' }}>
          {translate('form.ticket_order_hint', {
            _: 'Sürükleyip bırakarak sitedeki sırayı ayarlayın. Ürüne tıklayarak açıp kapatın.',
          })}
        </Typography>
      </Box>

      <Box sx={{ p: { xs: 1.5, sm: 2 }, flex: 1, minWidth: 0 }}>
        {flatItems.length === 0 ? (
          <Box
            sx={{
              py: 4,
              px: 2,
              textAlign: 'center',
              border: '1px dashed #d1d5db',
              borderRadius: '10px',
              bgcolor: '#f9fafb',
              mb: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {translate('form.no_general_tickets_yet', {
                _: 'Henüz bilet veya ürün eklenmedi. Tip seçip ekleyin.',
              })}
            </Typography>
          </Box>
        ) : (
          flatItems.map(({ group, index, item, id }, listIndex) => {
            const getSource = (field) => `${basePath}.${group}.${index}.${field}`;
            const expanded = expandedIds.has(id);
            const isDragging = dragId === id;
            const isDropTarget = Boolean(dragId) && overId === id && dragId !== id;
            const typeLabel = groupChoices.find((c) => c.id === group)?.name || group;
            const titleText = productTitlePreview(item, sourceLang);
            const priceText = item?.price != null && item.price !== '' ? `${item.price} TL` : null;

            return (
              <Box
                key={id}
                draggable={!disabled && armedId === id}
                onDragStart={handleDragStart(id)}
                onDragOver={handleDragOver(id)}
                onDrop={handleDrop(id)}
                onDragEnd={resetDrag}
                sx={{
                  ...PRODUCT_CARD_SX,
                  p: 0,
                  overflow: 'hidden',
                  opacity: isDragging ? 0.45 : 1,
                  borderColor: isDropTarget ? 'primary.main' : '#e8eaee',
                  boxShadow: isDropTarget ? '0 0 0 2px rgba(174,37,109,0.18)' : 'none',
                  transition: 'border-color 0.12s, box-shadow 0.12s, opacity 0.12s',
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  onClick={() => toggleExpanded(id)}
                  sx={{
                    px: 1.25,
                    py: 1,
                    cursor: 'pointer',
                    bgcolor: expanded ? '#fafbfc' : '#fff',
                    borderBottom: expanded ? '1px solid #eef0f3' : 'none',
                    userSelect: 'none',
                  }}
                >
                  {!disabled && (
                    <Tooltip
                      title={translate('form.drag_to_reorder', { _: 'Sürükleyerek sırala' })}
                    >
                      <Box
                        component="span"
                        onMouseDown={() => setArmedId(id)}
                        onMouseUp={() => setArmedId(null)}
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          display: 'inline-flex',
                          color: '#9ca3af',
                          cursor: 'grab',
                          '&:active': { cursor: 'grabbing' },
                          '&:hover': { color: '#4b5563' },
                        }}
                      >
                        <DragIndicatorIcon fontSize="small" />
                      </Box>
                    </Tooltip>
                  )}
                  <Typography
                    variant="caption"
                    sx={{
                      minWidth: 24,
                      height: 24,
                      borderRadius: '6px',
                      bgcolor: '#f3f4f6',
                      color: '#4b5563',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    {listIndex + 1}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    noWrap
                    sx={{ flex: 1, minWidth: 0, color: titleText ? '#111827' : '#9ca3af' }}
                  >
                    {titleText || translate('form.untitled_product', { _: 'İsimsiz ürün' })}
                  </Typography>
                  <Chip
                    size="small"
                    label={typeLabel}
                    sx={{ height: 22, fontSize: 11, fontWeight: 600, bgcolor: '#f3f4f6' }}
                  />
                  {priceText ? (
                    <Typography
                      variant="caption"
                      sx={{ color: '#4b5563', fontWeight: 600, whiteSpace: 'nowrap' }}
                    >
                      {priceText}
                    </Typography>
                  ) : null}
                  {!disabled && (
                    <Tooltip title={translate('ra.action.delete', { _: 'Sil' })}>
                      <IconButton
                        type="button"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemove(group, index, id)(e);
                        }}
                        aria-label="remove"
                        sx={{
                          color: '#9ca3af',
                          '&:hover': { color: '#dc2626', bgcolor: 'rgba(220,38,38,0.06)' },
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <ExpandMoreIcon
                    fontSize="small"
                    sx={{
                      color: '#6b7280',
                      transition: 'transform 0.15s',
                      transform: expanded ? 'rotate(180deg)' : 'none',
                    }}
                  />
                </Stack>

                <Collapse in={expanded} timeout={150} unmountOnExit>
                  <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <FormControl size="small" fullWidth sx={{ mb: 1.5 }} disabled={disabled}>
                      <InputLabel id={`ticket-group-${id}-label`}>
                        {translate('form.ticket_group_type', { _: 'Bilet / ürün tipi' })}
                      </InputLabel>
                      <Select
                        labelId={`ticket-group-${id}-label`}
                        label={translate('form.ticket_group_type', { _: 'Bilet / ürün tipi' })}
                        value={group}
                        onChange={(e) => handleChangeGroup(group, index, e.target.value)}
                      >
                        {groupChoices.map((c) => (
                          <MenuItem key={c.id} value={c.id}>
                            {c.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <StandardProductFormFields
                      getSource={getSource}
                      scopedFormData={item || {}}
                      disabled={disabled}
                      sourceLang={sourceLang}
                      isEdit={isEdit}
                      refundAction={
                        !disabled && listIndex < flatItems.length - 1 ? (
                          <Tooltip
                            title={translate('form.apply_refund_below_hint', {
                              _: 'İade ayarını yalnızca bu ürünün altındaki ürünlere kopyalar.',
                            })}
                          >
                            <Button
                              type="button"
                              size="small"
                              variant="outlined"
                              onClick={() => handleApplyRefundBelow(listIndex, group, index)}
                              sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                                borderRadius: '8px',
                                flexShrink: 0,
                                mt: '13px',
                              }}
                            >
                              {translate('form.apply_to_all', { _: 'Tümüne uygula' })}
                            </Button>
                          </Tooltip>
                        ) : null
                      }
                    />
                  </Box>
                </Collapse>
              </Box>
            );
          })
        )}

        {!disabled && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexWrap: 'wrap',
              pt: 1.5,
              mt: flatItems.length ? 0.5 : 0,
              borderTop: flatItems.length ? '1px solid #eef0f3' : 'none',
            }}
          >
            <FormControl
              size="small"
              sx={{ minWidth: { xs: '100%', sm: 220 }, flex: { sm: '1 1 200px' } }}
            >
              <InputLabel id="unified-ticket-add-type-label">
                {translate('form.ticket_group_type', { _: 'Bilet / ürün tipi' })}
              </InputLabel>
              <Select
                labelId="unified-ticket-add-type-label"
                label={translate('form.ticket_group_type', { _: 'Bilet / ürün tipi' })}
                value={addGroup}
                onChange={(e) => setAddGroup(e.target.value)}
              >
                {groupChoices.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              type="button"
              variant="contained"
              startIcon={<AddCircleOutlineIcon />}
              onClick={handleAdd}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                px: 2,
                borderRadius: '10px',
                boxShadow: 'none',
                '&:hover': { boxShadow: 'none' },
              }}
            >
              {translate('form.add_ticket_product', { _: 'Ürün Ekle' })}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

/**
 * Product Form Section Component
 * Individual product form section with title and form fields
 */
const ProductFormSection = ({
  title,
  source,
  disabled = false,
  helperText = null,
  sourceLang,
  isEdit = false,
  isInvitation = false,
}) => {
  const translate = useTranslate();
  const { getValues, setValue } = useFormContext();
  const watched = useWatch({ name: source });
  const items = Array.isArray(watched) ? watched : [];

  const handleAdd = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (disabled) return;
    const current = getValues(source);
    const list = Array.isArray(current) ? [...current] : [];
    list.push(emptyProduct());
    setValue(source, list, { shouldDirty: true, shouldTouch: true });
  };

  const handleRemove = (index) => (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (disabled) return;
    const current = getValues(source);
    const list = Array.isArray(current) ? [...current] : [];
    list.splice(index, 1);
    setValue(source, list, { shouldDirty: true, shouldTouch: true });
  };

  return (
    <Box sx={PANEL_SX}>
      <Box sx={PANEL_HEADER_SX}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ m: 0, color: '#111827' }}>
          {title}
        </Typography>
        <Typography variant="caption" sx={{ color: '#b45309', fontStyle: 'italic' }}>
          {translate('form.all_fields_required', '(* all fields required)')}
        </Typography>
      </Box>
      <Box sx={{ p: { xs: 1.5, sm: 2 }, flex: 1, minWidth: 0 }}>
        {helperText ? (
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              px: 1.25,
              py: 0.75,
              borderRadius: '8px',
              bgcolor: '#0ABAB5',
              color: '#fff',
              display: 'inline-block',
              lineHeight: 1.4,
            }}
          >
            {helperText}
          </Typography>
        ) : null}
        {items.map((item, index) => (
          <Box
            key={item?.id || `${source}-${index}`}
            sx={{ ...PRODUCT_CARD_SX, position: 'relative' }}
          >
            {!disabled && (
              <Tooltip title={translate('ra.action.delete', { _: 'Sil' })}>
                <IconButton
                  type="button"
                  size="small"
                  onClick={handleRemove(index)}
                  aria-label="remove"
                  sx={{
                    position: 'absolute',
                    top: 6,
                    right: 6,
                    zIndex: 1,
                    color: '#9ca3af',
                    '&:hover': { color: '#dc2626', bgcolor: 'rgba(220,38,38,0.06)' },
                  }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <StandardProductFormFields
              getSource={(field) => `${source}.${index}.${field}`}
              scopedFormData={item || {}}
              disabled={disabled}
              sourceLang={sourceLang}
              isEdit={isEdit}
              isInvitation={isInvitation}
            />
          </Box>
        ))}
        {!disabled && (
          <Button
            type="button"
            variant="outlined"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleAdd}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px' }}
          >
            {translate('form.add_ticket_product', { _: 'Ürün Ekle' })}
          </Button>
        )}
      </Box>
    </Box>
  );
};

const StandardProductFormFields = ({
  getSource,
  scopedFormData,
  disabled = false,
  sourceLang,
  refundAction,
  isEdit = false,
  isInvitation = false,
}) => {
  return (
    <SharedStandardProductForm
      getSource={getSource}
      showSold={false}
      scopedFormData={scopedFormData || {}}
      isEditForm={isEdit}
      disabled={disabled}
      sourceLang={sourceLang}
      refundAction={refundAction}
      hideTicketId={!isEdit}
      isInvitation={isInvitation}
    />
  );
};

const SeatedProductFormFields = ({ getSource, scopedFormData, disabled = false, sourceLang }) => {
  return (
    <SharedSeatedProductForm
      getSource={getSource}
      showSold={false}
      scopedFormData={scopedFormData || {}}
      isEditForm={false}
      disabled={disabled}
      sourceLang={sourceLang}
      hideTicketId
    />
  );
};
